import { View, Dialog } from 'web-mojo';
import SplitRouteModal from '@portal/routes/views/SplitRouteModal.js';
import StopInsertionDrawer from '@portal/routes/views/StopInsertionDrawer.js';
import ContactOperatorModal from '@portal/routes/views/ContactOperatorModal.js';

class StopReassignPanel extends View {
    constructor({ stop, operators, route }) {
        super();
        this.stop = stop;
        this.operators = operators;
        this.route = route;
    }

    async onActionSave() {
        const operatorId = this.element.querySelector('[name=operator]')?.value;
        if (!operatorId) {
            app.toast.warning('Select an operator');
            return;
        }
        // Transfer this stop to another route for the selected operator via split
        const response = await app.api.patch(`/api/routes/route/${this.route.id}`, {
            action: 'split',
            stop_ids: [this.stop.id],
            target_operator_id: parseInt(operatorId),
        });
        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to reassign stop');
            return;
        }
        this.emit('reassigned');
    }

    set template(v) { this._t = v; }

    get template() {
        const options = this.operators.map(op =>
            `<option value="${op.id}">${op.display_name || op.id}</option>`
        ).join('');
        return `
            <div class="p-3">
                <p class="small text-muted mb-3">This stop will be moved to a new route for the selected operator.</p>
                <div class="mb-3">
                    <label class="form-label">Assign to</label>
                    <select class="form-select" name="operator">
                        <option value="">Select operator…</option>
                        ${options}
                    </select>
                </div>
                <div class="d-flex justify-content-end">
                    <button type="button" class="btn btn-primary" data-action="save">Reassign</button>
                </div>
            </div>
        `;
    }
}

const STOP_STATUS_ICON = {
    pending: '<i class="bi bi-circle text-muted"></i>',
    arrived: '<i class="bi bi-circle-fill text-primary"></i>',
    in_progress: '<i class="bi bi-circle-fill text-warning"></i>',
    completed: '<i class="bi bi-check-circle-fill text-success"></i>',
    skipped: '<i class="bi bi-skip-forward-fill text-secondary"></i>',
    transfer_requested: '<i class="bi bi-arrow-right-circle-fill text-warning"></i>',
    removed: '<i class="bi bi-x-circle-fill text-danger"></i>',
};

export default class OperatorProgressPanel extends View {
    constructor({ routeId }) {
        super();
        this.routeId = routeId;
        this.route = null;
        this._wsHandler = this._onStopUpdate.bind(this);
    }

    async onInit() {
        app.events.on('route:stop_update', this._wsHandler);
        app.events.on('route:stop_inserted', this._wsHandler);
        app.events.on('route:stop_removed', this._wsHandler);
        await this.loadRoute();
    }

    onDestroy() {
        app.events.off('route:stop_update', this._wsHandler);
        app.events.off('route:stop_inserted', this._wsHandler);
        app.events.off('route:stop_removed', this._wsHandler);
    }

    async _onStopUpdate(data) {
        if (String(data.route_id) === String(this.routeId)) {
            await this.loadRoute();
        }
    }

    async loadRoute() {
        const response = await app.api.get(`/api/routes/route/${this.routeId}`, { graph: 'detail' });
        if (!response.success || !response.data.status) {
            app.toast.error('Failed to load route detail');
            return;
        }
        this.route = response.data.data;

        // graph=detail should include stops_list; if not, fetch stops directly
        if (!this.route.stops_list?.length) {
            const stopsRes = await app.api.get('/api/routes/stop', {
                route: this.routeId,
                size: 200,
                sort: 'sequence_position',
            });
            if (stopsRes.success && stopsRes.data.status) {
                this.route.stops_list = stopsRes.data.data || [];
            }
        }

        this.render();
    }

    onActionSplitRoute() {
        const r = this.route;
        if (!r) return;
        const modal = new SplitRouteModal({ route: r });
        const dialog = Dialog.showDialog({
            title: `Split Route — ${r.template?.name || 'Route'}`,
            body: modal,
            size: 'lg',
            footer: false,
        });
        modal.on('split', async () => {
            dialog.hide();
            app.toast.success('Route split successfully');
            await this.loadRoute();
        });
    }

    onActionContactOperator() {
        const r = this.route;
        if (!r) return;
        const modal = new ContactOperatorModal({ operator: r.operator });
        Dialog.showDialog({
            title: `Contact — ${r.operator?.display_name || 'Operator'}`,
            body: modal,
            size: 'sm',
            footer: false,
        });
    }

    onActionAddStop() {
        const r = this.route;
        if (!r) return;
        const stops = r.stops_list || [];
        const drawer = new StopInsertionDrawer({ route: r, stops });
        const dialog = Dialog.showDialog({
            title: 'Insert Stop',
            body: drawer,
            size: 'md',
            footer: false,
        });
        drawer.on('inserted', async () => {
            dialog.hide();
            app.toast.success('Stop inserted');
            await this.loadRoute();
        });
    }

    async onActionReassignStop(event, el) {
        const stopId = el.getAttribute('data-stop-id');
        const stop = (this.route?.stops_list || []).find(s => String(s.id) === stopId);
        if (!stop) return;

        // Load active operators for this group
        const opResponse = await app.api.get('/api/routes/operator', {
            group: app.groupId(),
            status: 'active',
            size: 200,
        });
        const operators = (opResponse.success && opResponse.data.status)
            ? (opResponse.data.data || []).filter(op => op.id !== this.route?.operator?.id)
            : [];

        const panel = new StopReassignPanel({ stop, operators, route: this.route });
        const dialog = Dialog.showDialog({
            title: `Reassign — ${stop.label || stop.location?.name || 'Stop'}`,
            body: panel,
            size: 'sm',
            footer: false,
        });
        panel.on('reassigned', async () => {
            dialog.hide();
            app.toast.success('Stop reassigned');
            await this.loadRoute();
        });
    }

    async onActionSkipStop(event, el) {
        const stopId = el.getAttribute('data-stop-id');
        const stop = (this.route?.stops_list || []).find(s => String(s.id) === stopId);
        if (!stop) return;

        const label = stop.label || stop.location?.name || 'this stop';
        const confirmed = await Dialog.confirm({
            title: 'Skip Stop',
            message: `Mark "${label}" as skipped? The operator will be notified.`,
            confirmText: 'Skip Stop',
            confirmClass: 'btn-warning',
        });
        if (!confirmed) return;

        const response = await app.api.patch(`/api/routes/stop/${stopId}`, {
            action: 'skip',
            reason: 'Skipped by planner',
            reason_code: 'planner_skip',
        });
        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to skip stop');
            return;
        }
        app.toast.success('Stop skipped');
        await this.loadRoute();
    }

    _formatTime(iso) {
        if (!iso) return '';
        return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    }

    _formatDuration(fromIso, toIso) {
        if (!fromIso || !toIso) return '';
        const mins = Math.round((new Date(toIso) - new Date(fromIso)) / 60000);
        if (mins < 60) return `${mins}m`;
        return `${Math.floor(mins / 60)}h ${mins % 60}m`;
    }

    _timeAgo(iso) {
        if (!iso) return '';
        const mins = Math.round((Date.now() - new Date(iso)) / 60000);
        if (mins < 1) return 'just now';
        if (mins < 60) return `${mins}m ago`;
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        return m > 0 ? `${h}h ${m}m ago` : `${h}h ago`;
    }

    set template(v) { this._t = v; }

    get template() {
        if (!this.route) {
            return `<div class="text-center py-4 text-muted"><div class="spinner-border spinner-border-sm"></div></div>`;
        }

        const r = this.route;
        const stops = r.stops_list || [];
        const operator = r.operator;
        const progress = r.progress;
        const isLive = r.status === 'in_progress';
        const isActive = ['in_progress', 'scheduled'].includes(r.status);

        const STATUS_BADGE = {
            scheduled:   '<span class="badge bg-secondary">Scheduled</span>',
            in_progress: '<span class="badge bg-primary">In Progress</span>',
            completed:   '<span class="badge bg-success">Completed</span>',
            cancelled:   '<span class="badge bg-dark">Cancelled</span>',
        };

        const pct = (progress?.total)
            ? Math.round(progress.completed / progress.total * 100) : 0;
        const skipped = stops.filter(s => s.status === 'skipped').length;
        const startedAt = r.started_at || r.created_at;

        // ── Header ────────────────────────────────────────────────────────────
        const headerHtml = `
            <div class="px-3 pt-3 pb-2 border-bottom">
                <div class="d-flex align-items-start justify-content-between mb-2">
                    <div>
                        <div class="fw-semibold">${r.template?.name || `Route ${r.id}`}</div>
                        <div class="text-muted small">
                            <i class="bi bi-calendar3 me-1"></i>${r.scheduled_date || ''}
                            ${startedAt && isLive ? `<span class="ms-2"><i class="bi bi-clock me-1"></i>Started ${this._timeAgo(startedAt)}</span>` : ''}
                        </div>
                    </div>
                    <div class="d-flex align-items-center gap-2">
                        ${STATUS_BADGE[r.status] || ''}
                        ${isLive ? `
                            <button type="button" class="btn btn-sm btn-outline-secondary" data-action="add-stop" title="Insert Stop">
                                <i class="bi bi-plus-lg"></i>
                            </button>
                            <button type="button" class="btn btn-sm btn-outline-secondary" data-action="split-route" title="Split Route">
                                <i class="bi bi-scissors"></i>
                            </button>
                        ` : ''}
                    </div>
                </div>

                <div class="d-flex align-items-center justify-content-between">
                    <div class="d-flex align-items-center gap-2">
                        ${operator ? `
                            <i class="bi bi-person-circle text-muted"></i>
                            <div>
                                <span class="small fw-semibold">${operator.display_name || operator.user?.username || 'Operator'}</span>
                                ${operator.phone ? `<span class="text-muted small ms-2"><i class="bi bi-phone me-1"></i>${operator.phone}</span>` : ''}
                                ${operator.user?.email ? `<span class="text-muted small ms-2 d-none d-md-inline">${operator.user.email}</span>` : ''}
                            </div>
                        ` : '<span class="text-warning small"><i class="bi bi-exclamation-triangle me-1"></i>No operator</span>'}
                    </div>
                    ${operator ? `
                        <button type="button" class="btn btn-sm btn-outline-secondary" data-action="contact-operator">
                            <i class="bi bi-chat me-1"></i>Contact
                        </button>
                    ` : ''}
                </div>
            </div>
        `;

        // ── Progress stats ────────────────────────────────────────────────────
        const statsHtml = progress ? `
            <div class="px-3 py-2 border-bottom bg-light">
                <div class="d-flex gap-4 small mb-2">
                    <div class="text-center">
                        <div class="fw-bold fs-6">${progress.completed}</div>
                        <div class="text-muted">Done</div>
                    </div>
                    <div class="text-center">
                        <div class="fw-bold fs-6">${progress.total - progress.completed - skipped}</div>
                        <div class="text-muted">Remaining</div>
                    </div>
                    ${skipped > 0 ? `
                        <div class="text-center">
                            <div class="fw-bold fs-6 text-secondary">${skipped}</div>
                            <div class="text-muted">Skipped</div>
                        </div>
                    ` : ''}
                    <div class="text-center">
                        <div class="fw-bold fs-6">${progress.total}</div>
                        <div class="text-muted">Total</div>
                    </div>
                </div>
                <div class="d-flex align-items-center gap-2">
                    <div class="progress flex-grow-1" style="height:8px;">
                        <div class="progress-bar ${r.status === 'completed' ? 'bg-success' : ''}"
                            style="width:${pct}%"></div>
                    </div>
                    <span class="small text-muted fw-semibold" style="min-width:2.5rem;">${pct}%</span>
                </div>
            </div>
        ` : '';

        // ── Stop rows ─────────────────────────────────────────────────────────
        const stopRows = stops.map((stop, i) => {
            const icon = STOP_STATUS_ICON[stop.status] || STOP_STATUS_ICON.pending;
            const label = stop.label || stop.location?.name || `Stop ${i + 1}`;
            const address = stop.location?.address || stop.address || '';
            const machine = stop.machine?.name || '';
            const activity = stop.activity_type || '';
            const timeIn = this._formatTime(stop.checked_in_at);
            const timeOut = this._formatTime(stop.completed_at);
            const duration = this._formatDuration(stop.checked_in_at, stop.completed_at);
            const planned = stop.dwell_minutes ? `${stop.dwell_minutes}m planned` : '';
            const plannerNote = stop.planner_note || '';
            const hasException = !!stop.exception;

            const isCompleted = stop.status === 'completed';
            const isSkipped = stop.status === 'skipped';
            const isDone = isCompleted || isSkipped || stop.status === 'removed';
            const isActionable = isActive && (stop.status === 'pending' || stop.status === 'arrived');
            const isCurrent = stop.status === 'in_progress' || stop.status === 'arrived';

            const rowBg = isCurrent ? 'style="background:rgba(13,110,253,0.04);"' : '';

            return `
                <div class="px-3 py-2 border-bottom ${isDone ? 'opacity-75' : ''}" ${rowBg}>
                    <div class="d-flex align-items-start gap-2">
                        <div class="pt-1 flex-shrink-0">${icon}</div>
                        <div class="small text-muted flex-shrink-0" style="min-width:1.4rem; padding-top:1px;">${i + 1}</div>
                        <div class="flex-grow-1 min-width-0">
                            <div class="d-flex align-items-start justify-content-between gap-1">
                                <div>
                                    <span class="small fw-semibold">${label}</span>
                                    ${hasException ? `<i class="bi bi-exclamation-triangle-fill text-warning ms-1 small" title="Exception reported"></i>` : ''}
                                    ${activity ? `<span class="badge bg-light text-dark border ms-1" style="font-size:0.65rem;">${activity}</span>` : ''}
                                </div>
                                ${isActionable ? `
                                    <div class="dropdown flex-shrink-0">
                                        <button type="button" class="btn btn-sm btn-link text-muted p-0"
                                            data-bs-toggle="dropdown" aria-expanded="false">
                                            <i class="bi bi-three-dots-vertical"></i>
                                        </button>
                                        <ul class="dropdown-menu dropdown-menu-end">
                                            <li><a class="dropdown-item small" href="#"
                                                data-action="reassign-stop" data-stop-id="${stop.id}">
                                                <i class="bi bi-person-arrows me-2"></i>Reassign
                                            </a></li>
                                            <li><a class="dropdown-item small" href="#"
                                                data-action="skip-stop" data-stop-id="${stop.id}">
                                                <i class="bi bi-skip-forward me-2"></i>Skip
                                            </a></li>
                                        </ul>
                                    </div>
                                ` : ''}
                            </div>
                            ${address ? `<div class="text-muted small"><i class="bi bi-geo-alt me-1"></i>${address}</div>` : ''}
                            ${machine ? `<div class="text-muted small"><i class="bi bi-hdd me-1"></i>${machine}</div>` : ''}
                            <div class="text-muted small d-flex flex-wrap gap-2 mt-1">
                                ${timeIn && timeOut ? `
                                    <span><i class="bi bi-clock me-1"></i>${timeIn} – ${timeOut}${duration ? ` (${duration})` : ''}</span>
                                ` : timeIn ? `
                                    <span><i class="bi bi-box-arrow-in-right me-1"></i>In at ${timeIn}</span>
                                ` : ''}
                                ${planned && !isCompleted ? `<span class="text-secondary">${planned}</span>` : ''}
                                ${isSkipped ? `<span class="text-secondary fst-italic">Skipped</span>` : ''}
                            </div>
                            ${plannerNote ? `
                                <div class="mt-1 small fst-italic text-secondary border-start border-2 ps-2">
                                    ${plannerNote}
                                </div>
                            ` : ''}
                        </div>
                    </div>
                </div>
            `;
        }).join('');

        return `
            <div>
                ${headerHtml}
                ${statsHtml}
                ${stops.length > 0
                    ? `<div style="max-height:420px; overflow-y:auto;">${stopRows}</div>`
                    : `<div class="text-center text-muted py-4 small">No stops loaded.</div>`
                }
            </div>
        `;
    }
}
